import React from 'react';
import type { ColumnBlock, ColumnSection } from 'libs/sanity/types/generated';
import CmsBlocks from '@fogia/core-web/libs/sanity/content/CmsBlocks';
import BlockGridWrapper from 'components/wrappers/BlockGridWrapper';
import styled from 'libs/styled';
import { above } from '@fogia/core-web/utils/mediaqueries';

type LayoutVariant = 'quarterWidth' | 'halfWidth';
type SectionSize = 'quarterWidth' | 'halfWidth';

interface ColumnModuleProps {
    data: {
        block?: ColumnBlock;
    };
    imageLoading?: 'eager' | 'lazy';
}

const MODULE_MARGIN_Y = 'var(--module-margin-y)';
const TIGHT_MOBILE_MARGIN = '10px';

const ModuleWrapper = styled('div')`
    height: auto;
`;

const SectionSlot = styled('div')`
    min-height: 0;

    ${above['tablet.lg']} {
        align-self: stretch;
    }
`;

const StickyContent = styled('div')`
    ${above['tablet.lg']} {
        position: sticky;
        top: 10px;
    }
`;

const getBlockType = (section: ColumnSection | undefined): string | undefined => section?.blocks?.[0]?.type;

const ColumnModule: React.FC<ColumnModuleProps> = (props) => {
    const { data: moduleData, imageLoading = 'lazy' } = props;
    const block = moduleData?.block;
    const sections = block?.sections ?? [];

    if (sections.length < 2) {
        return null;
    }

    /** Adds layout (quarterWidth | halfWidth) to each block so CTA and other blocks render the correct width variant. */
    const addLayoutToBlocks = (
        blocks: ColumnSection['blocks'],
        size: SectionSize,
    ): Array<Record<string, unknown> & { layout: LayoutVariant }> => {
        const layout: LayoutVariant = size;
        if (!Array.isArray(blocks)) return [];
        return blocks.map((block) => ({ ...block, layout }));
    };

    const leftSection = sections[0];
    const rightSection = sections[1];
    const leftSize = (leftSection?.size ?? 'halfWidth') as SectionSize;
    const rightSize = (rightSection?.size ?? 'halfWidth') as SectionSize;
    const isLeftHalfWidth = leftSize === 'halfWidth';
    const isRightHalfWidth = rightSize === 'halfWidth';
    const leftBlocks = addLayoutToBlocks(leftSection?.blocks, leftSize);
    const rightBlocks = addLayoutToBlocks(rightSection?.blocks, rightSize);

    const isLeftCta = getBlockType(leftSection) === 'callToActionBlock';
    const isRightCta = getBlockType(rightSection) === 'callToActionBlock';

    const isTopHalfWidthCta = isLeftHalfWidth && isLeftCta;
    const isBottomHalfWidthCta = isRightHalfWidth && isRightCta;

    const rowGap = isTopHalfWidthCta && isBottomHalfWidthCta ? TIGHT_MOBILE_MARGIN : MODULE_MARGIN_Y;

    return (
        <ModuleWrapper margin={`${MODULE_MARGIN_Y} 0`}>
            <BlockGridWrapper rowGap={rowGap}>
                {leftSection && leftBlocks.length > 0 && (
                    <SectionSlot
                        gridColumn={[
                            isLeftHalfWidth ? '1/-1' : '3/11',
                            null,
                            null,
                            null,
                            isLeftHalfWidth ? '1 / 13' : '1 / span 6',
                        ]}
                    >
                        <StickyContent>
                            <CmsBlocks data={leftBlocks} imageLoading={imageLoading} />
                        </StickyContent>
                    </SectionSlot>
                )}
                {rightSection && rightBlocks.length > 0 && (
                    <SectionSlot
                        gridColumn={[
                            isRightHalfWidth ? '1/-1' : '3/11',
                            null,
                            null,
                            null,
                            isRightHalfWidth ? '13 / 25' : '19 / span 6',
                        ]}
                    >
                        <StickyContent>
                            <CmsBlocks data={rightBlocks} imageLoading={imageLoading} />
                        </StickyContent>
                    </SectionSlot>
                )}
            </BlockGridWrapper>
        </ModuleWrapper>
    );
};

export default ColumnModule;
